import { QueryClient } from '@tanstack/react-query'
import { fetchPage } from '@vangst/services/oogst/page'
import useGetSalaryGuide from '@vangst/services/oogst/registrations/useGetSalaryGuide'
import { RobotsEnum } from '@vangst/services/oogst/types'
import useViewer from '@vangst/services/oogst/viewer/useViewer'
import Routes from '@vangst/services/routes'
import { setSessionRedirect, StorageKeys } from '@vangst/services/storage'
import { InferGetStaticPropsType } from 'next'
import React, { useCallback, useState } from 'react'
import { MdDescription } from 'react-icons/md'
import Busy from '../../components/feedback/Busy'
import Breadcrumbs from '../../components/navigation/Breadcrumbs'
import ClickyBtn from '../../components/navigation/ClickyBtn'
import ClickyLink from '../../components/navigation/ClickyLink'
import HubSpotForm, {
  HubSpotFormIDs,
} from '../../domains/application/HubSpotForm'
import Meta from '../../domains/application/Meta'
// import NewsletterForm from '../../domains/application/NewsletterForm'

import AlertDialogSignInOrUp from '../../domains/viewer/feedback/DialogSignInOrUp'

export async function getStaticProps() {
  const queryClient = new QueryClient()
  const rawUrl = __filename.split('pages')[1]?.replace('.js', '')
  const page = await fetchPage(queryClient, { rawUrl })
  const crumbs = [
    { href: Routes.INDEX, text: 'Home' },
    { href: Routes.REPORTS, text: 'Reports' },
    { href: null, text: 'Salary Guide' },
  ]

  return {
    props: {
      crumbs,
      page: page?.getPage ? page.getPage : null,
    },
    revalidate: 30,
  }
}

// -------------------------------------

type PropsType = InferGetStaticPropsType<typeof getStaticProps>

export default function SalaryGuide(props: PropsType) {
  const [showDialog, setShowDialog] = useState(false)
  const { crumbs, page } = props
  const { authenticated, authenticating } = useViewer()

  const [scene, setScene] = React.useState<1 | 2 | undefined>(1)

  const { data, isLoading } = useGetSalaryGuide(undefined, {
    enabled: authenticated || scene === 2,
  })

  React.useEffect(() => {
    authenticated && setScene(2)
    if (globalThis.sessionStorage?.getItem(StorageKeys.REDIRECT) == null) {
      setSessionRedirect(Routes.REPORTS_SALARY_GUIDE)
    }
  }, [authenticated, scene])

  const downloadLink = data?.getReport?.url

  const handleSubmit = useCallback(() => {
    setScene(2)
  }, [])

  return (
    <>
      <Meta
        key="meta"
        canonicalUrl={page?.metaInfo.canonicalUrl}
        crumbs={crumbs}
        description={page?.metaInfo.description}
        publishDate={page?.postedAt}
        robots={page?.metaInfo.robots ?? RobotsEnum.IndexFollow}
        title={page?.metaInfo.title ?? page?.title ?? '2024 Salary Guide'}
      />
      <Breadcrumbs crumbs={crumbs} />
      <article className="contain-xyc mood-orange min-h-screen">
        <div className="contain-xyc mood-white rounded-md border">
          <h1 className="heading-xl">The 2024 Salary Guide</h1>
          <div className="mt-8 grid content-start gap-12 sm:grid-cols-2">
            <div className="flow-y-sm">
              <p>
                Discover the latest hiring trends and insight into the
                professional landscape of the cannabis industry with the Vangst
                2024 Salary Guide. From salary data across various employment
                verticals to predictions of the year ahead, this report provides
                invaluable foresight for investors, employers, and job seekers
                alike.
              </p>
              <p>
                With data from over 1,000 cannabis professionals and companies,
                as well as expert analysis from the Vangst team, the report
                highlights:
              </p>

              <ul className="ml-4 flex list-disc flex-col gap-2">
                <li>
                  Salaries for 60 roles, spanning 10 verticals that represent
                  the industry
                </li>
                <li>Diversity and Benefits insights</li>
                <li>Trending industry topics</li>
                <li>
                  Predictions for 2024, including the state of cannabis
                  legalization
                </li>
              </ul>
            </div>
            <div className="grid content-start">
              {(scene == null || authenticating) && (
                <Busy className="m-auto w-72 self-start" />
              )}
              {scene === 1 && !authenticating && !authenticated && (
                <>
                  <p>
                    Tell us a little about yourself to access the full report.
                  </p>
                  <HubSpotForm
                    id="salary-guide-2024"
                    formId={HubSpotFormIDs.Report}
                    onSuccessCallback={handleSubmit}
                  />
                  <span className="mt-8 text-sm">
                    Already a member of Vangst or interested in joining?{' '}
                    <ClickyLink
                      onClick={() => setShowDialog(true)}
                      className="link-orange"
                    >
                      Sign In or Sign Up
                    </ClickyLink>{' '}
                    to view the PDF.
                  </span>
                  {showDialog && (
                    <AlertDialogSignInOrUp
                      handleClose={() => setShowDialog(false)}
                    />
                  )}
                </>
              )}
              {scene === 2 && (
                <ClickyBtn
                  as="text-icon"
                  className="mood-orange m-auto w-72 self-start"
                  disabled={isLoading}
                  href={downloadLink}
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  <span className="flex items-center gap-2">
                    <MdDescription />
                    <span>Download Salary Guide</span>
                  </span>
                </ClickyBtn>
              )}
            </div>
          </div>
        </div>
      </article>
    </>
  )
}
